import { useEffect, useState, type FC } from 'react';
import { useCustomMutation, useInvalidate } from '@refinedev/core';
import { Modal, Form, Input, Select, DatePicker, Button, Popconfirm, message } from 'antd';
import { Sparkles, Loader2, Trash2 } from 'lucide-react';
import dayjs, { type Dayjs } from 'dayjs';
import type { CalendarEntryData, EntryType, EntryStatus } from './CalendarEntry';

const TYPE_OPTIONS = [
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'email', label: 'Email' },
  { value: 'thread', label: 'Thread' },
  { value: 'blog', label: 'Blog' },
];

const PERSONA_OPTIONS = [
  { value: 'cto', label: 'CTO' },
  { value: 'product_owner', label: 'Product Owner' },
  { value: 'ceo_startup', label: 'CEO Startup' },
  { value: 'qa_lead', label: 'QA Lead' },
  { value: 'general', label: 'Général' },
];

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Brouillon' },
  { value: 'scheduled', label: 'Planifié' },
  { value: 'published', label: 'Publié' },
];

interface FormValues {
  title: string;
  type: EntryType;
  persona: string;
  scheduledDate: Dayjs;
  description?: string;
  status: EntryStatus;
}

interface CreateEntryModalProps {
  open: boolean;
  entry: CalendarEntryData | null;
  defaultDate?: Dayjs | null;
  onClose: () => void;
}

const CreateEntryModal: FC<CreateEntryModalProps> = ({ open, entry, defaultDate, onClose }) => {
  const [form] = Form.useForm<FormValues>();
  const [generating, setGenerating] = useState(false);
  const isEdit = !!entry;
  const invalidate = useInvalidate();

  const { mutate: createEntry } = useCustomMutation();
  const { mutate: updateEntry } = useCustomMutation();
  const { mutate: generateAI } = useCustomMutation();

  useEffect(() => {
    if (open) {
      if (entry) {
        form.setFieldsValue({
          title: entry.title,
          type: entry.type,
          persona: entry.persona ?? 'general',
          scheduledDate: dayjs(entry.scheduledDate),
          description: entry.description ?? '',
          status: entry.status,
        });
      } else {
        form.resetFields();
        if (defaultDate) form.setFieldValue('scheduledDate', defaultDate);
      }
    }
  }, [open, entry, defaultDate, form]);

  const refreshCalendar = () => invalidate({ invalidates: ['all'], resource: 'calendar-month' });

  const handleGenerateAI = () => {
    if (!isEdit) {
      message.warning("Sauvegardez d'abord l'entrée pour utiliser la génération IA");
      return;
    }
    const description = form.getFieldValue('description') || form.getFieldValue('title') || '';
    setGenerating(true);
    generateAI(
      {
        url: `/api/v1/admin/marketing/calendar/${entry!.id}/generate`,
        method: 'post',
        values: { userInput: description },
      },
      {
        onSuccess: (response) => {
          const content = response?.data?.content ?? response?.data?.data?.content;
          if (content) form.setFieldValue('description', content);
          message.success('Contenu généré avec IA');
          setGenerating(false);
        },
        onError: () => {
          message.error('Erreur lors de la génération');
          setGenerating(false);
        },
      },
    );
  };

  const handleSave = (values: FormValues) => {
    const payload = {
      title: values.title,
      type: values.type,
      persona: values.persona,
      scheduledDate: values.scheduledDate.toISOString(),
      content: values.description ?? '',
    };

    if (isEdit) {
      updateEntry(
        { url: `/api/v1/admin/marketing/calendar/${entry!.id}`, method: 'patch', values: payload },
        {
          onSuccess: () => {
            message.success('Publication modifiée');
            refreshCalendar();
            onClose();
          },
          onError: () => message.error('Erreur lors de la modification'),
        },
      );
    } else {
      createEntry(
        { url: '/api/v1/admin/marketing/calendar', method: 'post', values: payload },
        {
          onSuccess: () => {
            message.success('Publication créée');
            refreshCalendar();
            onClose();
          },
          onError: () => message.error('Erreur lors de la création'),
        },
      );
    }
  };

  const handleDelete = () => {
    // L'API ne dispose pas d'un DELETE dédié — on pourrait PATCH status archived
    updateEntry(
      { url: `/api/v1/admin/marketing/calendar/${entry!.id}`, method: 'patch', values: { title: '[SUPPRIMÉ]' } },
      {
        onSuccess: () => {
          message.success('Publication supprimée');
          refreshCalendar();
          onClose();
        },
      },
    );
  };

  return (
    <Modal
      title={isEdit ? 'Modifier la publication' : 'Nouvelle publication'}
      open={open}
      onCancel={onClose}
      footer={null}
      width={560}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleSave} className="mt-4">
        <Form.Item name="title" label="Titre" rules={[{ required: true, message: 'Titre requis' }]}>
          <Input placeholder="Titre de la publication" />
        </Form.Item>

        <div className="grid grid-cols-2 gap-4">
          <Form.Item name="type" label="Type" rules={[{ required: true }]} initialValue="linkedin">
            <Select options={TYPE_OPTIONS} />
          </Form.Item>
          <Form.Item name="persona" label="Persona cible" rules={[{ required: true }]} initialValue="general">
            <Select options={PERSONA_OPTIONS} />
          </Form.Item>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Form.Item name="scheduledDate" label="Date planifiée" rules={[{ required: true }]}>
            <DatePicker className="w-full" format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="status" label="Statut" rules={[{ required: true }]} initialValue="draft">
            <Select options={STATUS_OPTIONS} />
          </Form.Item>
        </div>

        <Form.Item name="description" label="Sujet / Description">
          <Input.TextArea rows={3} placeholder="Décrivez le sujet de la publication..." />
        </Form.Item>

        {/* Bouton IA */}
        <div className="mb-4">
          <Button
            onClick={handleGenerateAI}
            disabled={generating || !isEdit}
            icon={generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
            className="flex items-center gap-1.5"
          >
            {generating ? 'Génération en cours…' : 'Générer avec IA'}
          </Button>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between border-t border-slate-100 pt-4 dark:border-slate-700">
          <div>
            {isEdit && (
              <Popconfirm title="Supprimer cette publication ?" onConfirm={handleDelete} okText="Oui" cancelText="Non">
                <Button danger icon={<Trash2 className="h-3.5 w-3.5" />}>Supprimer</Button>
              </Popconfirm>
            )}
          </div>
          <div className="flex gap-2">
            <Button onClick={onClose}>Annuler</Button>
            <Button type="primary" htmlType="submit">
              {isEdit ? 'Modifier' : 'Créer'}
            </Button>
          </div>
        </div>
      </Form>
    </Modal>
  );
};

export default CreateEntryModal;
